import clsx from "clsx";
import { FC, ReactNode, useRef, useState } from "react";
import { Icons } from "rujira.ui";
import { useIsMobile } from "../../hooks/useBreakpoint";
import { useStuck } from "../../hooks/useStuck";

export const STICKY_TOP = 72;

// How far past the stick point to land, so a scroll leaves the bar parked
// instead of settling on the boundary where it springs back to inset.
export const STUCK_OVERSHOOT = 2;

export const StickyBar: FC<{
  className?: string;
  ringed?: boolean;
  // Turn off stickiness if you need to. Eg: during page loading
  sticky?: boolean;
  collapseLabel?: string;
  onCollapse?: () => void;
  // Set while the bar holds something the reader is working in. Outranks the
  // remembered collapse, so re-sticking mid-use does not fold it away.
  expanded?: boolean;
  children: ReactNode;
}> = ({
  className,
  ringed,
  sticky = true,
  collapseLabel,
  onCollapse,
  expanded,
  children,
}) => {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // The sticky rules are mobile-only, so there is nothing to measure above it.
  const stuck = useStuck(ref, STICKY_TOP, sticky && isMobile);

  const showToggle = !!collapseLabel && stuck;
  const isCollapsed = showToggle && collapsed && !expanded;

  return (
    <div
      ref={ref}
      className={clsx("sticky-bar", className, {
        "sticky-bar--stick": sticky,
        "sticky-bar--ringed": ringed,
        "sticky-bar--stuck": stuck,
        "sticky-bar--collapsible": showToggle && !isCollapsed,
        "sticky-bar--collapsed": isCollapsed,
      })}>
      {!isCollapsed && children}
      {showToggle && (
        <button
          type="button"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? undefined : "Collapse"}
          onClick={() => {
            if (!isCollapsed) onCollapse?.();
            setCollapsed((c) => !c);
          }}
          className="sticky-bar__collapse transparent flex ai-c jc-c pointer">
          {isCollapsed ? (
            <>
              <span className="fs-12 condensed fw-500 nowrap">
                {collapseLabel}
              </span>
              <Icons.Plus className="w-1.5 h-1.5" />
            </>
          ) : (
            <Icons.Minus className="w-1.5 h-1.5" />
          )}
        </button>
      )}
    </div>
  );
};
